import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { buildPdfHtml } from 'hooks/usePdf';
import { REQUEST_SUBSIDY_CERTIFICATE_PDF } from '../api';

interface Params {
  subsidyRequestId: string;
  // The certificate was already generated and attached, so there is no need to generate it on open.
  hasCertificate?: boolean;
}

const ERROR_MESSAGE = 'Failed to generate certificate PDF. Please try again.';

export function useSubsidyCertificatePdf({ subsidyRequestId, hasCertificate = false }: Params) {
  // State (instead of a plain ref) so generation starts once the dialog content is actually mounted.
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const startedForRef = useRef<string | null>(null);
  const { showNotification } = useNotifications();

  const [requestCertificatePDF] = useMutation<{ requestSubsidyCertificatePDF: string | null }>(
    REQUEST_SUBSIDY_CERTIFICATE_PDF
  );

  const generate = useCallback(
    async (node: HTMLDivElement, setLoading: (value: boolean) => void) => {
      setLoading(true);
      try {
        const { data, errors } = await requestCertificatePDF({
          variables: { input: { html: buildPdfHtml(node), subsidyRequestId } },
          // Refresh hasCertificate in the requests list, which was fetched before the certificate existed.
          refetchQueries: ['GetSubsidyRequests'],
        });

        if (errors?.length || !data?.requestSubsidyCertificatePDF) {
          showNotification(ERROR_MESSAGE, { type: 'error' });
          return;
        }
        setPdfUrl(data.requestSubsidyCertificatePDF);
        return data.requestSubsidyCertificatePDF;
      } catch {
        showNotification(ERROR_MESSAGE, { type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [requestCertificatePDF, showNotification, subsidyRequestId]
  );

  // Generate and attach the certificate to the submission as soon as the dialog is shown.
  useEffect(() => {
    if (hasCertificate || !contentNode || startedForRef.current === subsidyRequestId) return;
    startedForRef.current = subsidyRequestId;
    setPdfUrl(null);
    generate(contentNode, setIsGenerating);
  }, [contentNode, subsidyRequestId, hasCertificate, generate]);

  // Opens the PDF. For an already attached certificate the backend only returns a signed URL; if a
  // previous generation failed, this acts as a retry.
  const handleRequestPdf = async () => {
    if (isGenerating || isOpening) return;
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
      return;
    }
    if (!contentNode) return;
    const url = await generate(contentNode, hasCertificate ? setIsOpening : setIsGenerating);
    if (url) window.open(url, '_blank');
  };

  return {
    contentRef: setContentNode,
    isGenerating,
    isOpening,
    isGenerated: hasCertificate || !!pdfUrl,
    handleRequestPdf,
  };
}
